import React, { useMemo, useState } from "react";
import { useRouter } from "next/router";
import { IconButton, cn } from "ui";
import {
  CommandSquareIcon,
  ExclamationIcon,
  PencilIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from "icons/outline";
import { toast } from "react-hot-toast";
import Layout from "../../../components/Layout";
import { axiosApiInstance } from "../../api/axios";
import { RiskRuleCard } from "../../../features/risk/components/RiskRuleCard";
import HeaderTabs from "../../../components/HeaderTabs";
import {
  GlobalSearch,
  useGlobalSearchQuery,
} from "../../../components/GlobalSearch";
import Loading from "../../../components/Loading";
import { Filters } from "../../../components/newFilters";
import { Pagination } from "../../../components/Pagination";
import { usePaginationParams } from "../../../hooks/usePaginationParams";
import { RiskRule } from "../../../features/risk/types";
import NoResults from "../../../components/NoResults/NoResults";
import { AddNewRuleDrawer } from "../../../features/risk/components/AddNewRuleDrawer";
import { EditRuleDrawer } from "../../../features/risk/components/EditRuleDrawer";
import AlertModal from "../../../components/AlertModal";
import { useMoneyHashQuery } from "../../../hooks/useMoneyHashQuery";
import { useRestoreScrollOnFetching } from "../../../hooks/useRestoreScrollOnFetching";
import { useMutation } from "@tanstack/react-query";
import { parseErrorsToArray } from "../../../utils/parseErrors";
import { ErrorPage } from "../../../components/ErrorPage";

const tabs = [
  {
    name: "Risk Lists",
    href: "/risk/lists",
    current: false,
  },
  {
    name: "Risk Rules",
    href: "/risk/rules",
    current: true,
  },
];

export default function RiskRulesPage() {
  const router = useRouter();

  const searchQuery = useGlobalSearchQuery();
  const [paginationParams] = usePaginationParams();

  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [showEditRule, setShowEditRule] = useState(false);
  const [ruleIdToDelete, setRuleIdToDelete] = useState<string | null>(null);

  const {
    data: rules,
    count,
    listMeta,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useMoneyHashQuery<Array<RiskRule>>({
    queryKey: ["riskRules", { ...paginationParams, ...router.query }],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get("/d/risk_management/riskrules/", {
          params: {
            ...paginationParams,
            ...router.query,
          },
          signal,
        })
        .then((res) => res.data),
    keepPreviousData: true,
  });

  const scrollRef = useRestoreScrollOnFetching(isFetching);

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleIdToDelete: string | null) =>
      axiosApiInstance.delete(
        `/d/risk_management/riskrules/${ruleIdToDelete}/`
      ),
    onSuccess: () => {
      toast.success("Rule deleted successfully");
      refetch();
    },
    onError: (error) =>
      toast.error(parseErrorsToArray(error)?.[0] || "Something went wrong!"),
    onSettled: () => setRuleIdToDelete(null),
  });

  const isFiltering = useMemo(
    () =>
      Boolean(
        searchQuery ||
          (listMeta?.filter_enabled &&
            listMeta?.filter_types.some(
              ({ field_name }) => !!router.query[field_name]
            ))
      ),
    [
      searchQuery,
      listMeta?.filter_enabled,
      listMeta?.filter_types,
      router.query,
    ]
  );

  const selectedRule = rules?.find((rule) => rule.id === selectedRowId);

  return (
    <Layout ref={scrollRef} metaTitle="Risk Management">
      <div className="w-full">
        <div className="">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <span className="pb-1 text-lg font-bold text-foundations-boldest">
                Manage your risk rules
              </span>
              <span className="text-sm font-normal text-foundations-subtle">
                Create a rule to trigger risk alert or apply action based on
                risk level
              </span>
            </div>
            <div className="flex items-end justify-end">
              {listMeta?.search_enabled && <GlobalSearch scope="Risk Rules" />}

              <AddNewRuleDrawer
                refreshTable={refetch}
                isAddEnabled={listMeta?.add_enabled}
              />
              {selectedRule && (
                <EditRuleDrawer
                  rule={selectedRule}
                  showEditRule={showEditRule}
                  setShowEditRule={setShowEditRule}
                  refreshTable={refetch}
                />
              )}
            </div>
          </div>

          <div>
            <HeaderTabs tabs={tabs} />

            {isLoading && (
              <span className="pt-6">
                <Loading />
              </span>
            )}

            <AlertModal
              isOpen={!!ruleIdToDelete}
              variant="danger"
              Icon={ExclamationIcon}
              title="Delete rule"
              description="Are you sure you want to delete this rule from the flow?"
              confirmLabel="Delete"
              onConfirm={() => deleteRuleMutation.mutate(ruleIdToDelete)}
              onClose={() => setRuleIdToDelete(null)}
            />
            {isError && <ErrorPage error={error} />}
            {!isError && (
              <>
                {rules?.length && !isLoading ? (
                  <div className="pt-6">
                    {listMeta?.filter_enabled && (
                      <div className="mt-3">
                        <Filters
                          filters={listMeta.filter_types}
                          recentStoreKey="risk-rules"
                        />
                      </div>
                    )}

                    <div className="mt-4 flex">
                      <div
                        className={cn(
                          "grid flex-1 gap-6",
                          selectedRowId
                            ? "grid-cols-1 lg:grid-cols-2"
                            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                        )}
                      >
                        {rules?.map((rule) => (
                          <RiskRuleCard
                            key={rule.id}
                            onClick={() => setSelectedRowId(rule.id)}
                            rule={rule}
                            deleteActionName={"Delete rule"}
                            onDeleteRule={(e) => {
                              e.stopPropagation();
                              setRuleIdToDelete(String(rule.id));
                            }}
                          />
                        ))}
                      </div>

                      {selectedRowId && (
                        <div
                          className="z-0 ml-8 bg-foundations-default"
                          style={{ width: "35%", minWidth: "316px" }}
                        >
                          <div className="overflow-hidden rounded-md border border-foundations-subtle">
                            <div className="space-y-6 px-6 py-4 sm:px-6">
                              <div className="-mx-6 flex items-center justify-between border-b border-foundations-subtle px-6 pb-4">
                                <div>
                                  <span className="text-xs font-normal uppercase text-foundations-subtle">
                                    Rule #{selectedRule?.id}
                                  </span>
                                  <p className="text-base font-normal text-foundations-bolder">
                                    {selectedRule?.name}
                                  </p>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <IconButton
                                    variant="secondary"
                                    size="xs"
                                    onClick={() => {
                                      setShowEditRule(true);
                                    }}
                                  >
                                    <PencilIcon
                                      className=""
                                      aria-hidden="true"
                                    />
                                  </IconButton>

                                  <IconButton
                                    variant="tertiary"
                                    shape="circle"
                                    className="text-foundations-subtler"
                                    onClick={() => setSelectedRowId(null)}
                                  >
                                    <span className="sr-only">Close panel</span>
                                    <XCircleIcon
                                      className="h-6 w-6"
                                      aria-hidden="true"
                                    />
                                  </IconButton>
                                </div>
                              </div>

                              <div>
                                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                                  Linked Accounts
                                </h3>
                                {selectedRule?.used_accounts?.length ? (
                                  selectedRule?.used_accounts.map((account) => (
                                    <div
                                      key={account.id}
                                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                                    >
                                      <p className="inline-flex h-5 w-5 items-center justify-center rounded bg-foundations-bolder text-xs font-medium uppercase text-foundations-inverse ">
                                        {account.name.slice(0, 2)}
                                      </p>

                                      <p>{account.name}</p>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm font-normal text-foundations-subtle">
                                    -
                                  </p>
                                )}
                              </div>

                              <div>
                                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                                  Linked Payment Flows
                                </h3>
                                {selectedRule?.flows?.length ? (
                                  selectedRule?.flows.map((flow) => (
                                    <div
                                      key={flow.id}
                                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                                    >
                                      <CommandSquareIcon className="h-5 w-5 text-foundations-subtler" />
                                      <p>{flow.name}</p>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm font-normal text-foundations-subtle">
                                    -
                                  </p>
                                )}
                              </div>

                              <div>
                                <h3 className="mb-2 text-sm font-normal capitalize text-foundations-subtle">
                                  Linked Risk Lists
                                </h3>
                                {selectedRule?.risk_lists?.length ? (
                                  selectedRule?.risk_lists.map((riskList) => (
                                    <div
                                      key={riskList.id}
                                      className="mb-2 flex items-center space-x-2 text-sm font-normal text-foundations-subtle"
                                    >
                                      <ShieldExclamationIcon className="h-5 w-5 text-foundations-subtler" />
                                      <p>{riskList.name}</p>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-sm font-normal text-foundations-subtle">
                                    -
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Pagination
                      total={Number(count)}
                      onPageChange={({ page, limit }) => {
                        router.replace({
                          query: { ...router.query, page, limit },
                        });
                      }}
                    />
                  </div>
                ) : null}

                {!isLoading && rules?.length === 0 && (
                  <NoResults
                    tableName="Risk rules"
                    isFiltering={isFiltering}
                    removeAllFilters={() => {
                      router.replace({ query: { page: 1 } });
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

RiskRulesPage.parentPath = "/risk/rules";
